import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Field, reduxForm, formValueSelector } from 'redux-form';
import withStyles from 'isomorphic-style-loader/withStyles';
import cx from 'classnames';
import Button from 'react-bootstrap/lib/Button';
import Form from 'react-bootstrap/lib/Form';
import FormControl from 'react-bootstrap/lib/FormControl';
import Modal from 'react-bootstrap/lib/Modal';

import DateRange from '../DateRange';
import Avatar from '../../Avatar';
import Loader from '../../Loader';

import validate from './validate';
import messages from '../../../locale/messages';
import { contactHostClose } from '../../../actions/message/contactHostModal';
import { directBookingRequest } from '../../../actions/booking/directBookingRequest';

import { TickSvgIcon } from '../../Common/Assets/SvgComponent/commonSvgIcon';
import s from './ContactHost.css';
import bt from '../../../components/commonStyle.css';


const selector = formValueSelector('ContactHostForm');

const renderFormControlTextArea = ({ input, label, meta: { touched, error }, children, className, placeholder }) => {
  const { formatMessage } = useIntl();

  return (
    <div>
      <FormControl
        {...input}
        className={className}
        componentClass="textarea"
        placeholder={placeholder}
      >
        {children}
      </FormControl>
      {touched && error && <span className={s.errorMessage}>{formatMessage(error)}</span>}
    </div>
  )
};

const ContactHost = (props) => {

  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const isLoading = useSelector(state => state?.viewListing?.isLoading);
  const showContactHostModal = useSelector(state => state?.viewListing?.showContactHostModal);
  const availability = useSelector(state => state?.viewListing?.availability);
  const maximumStay = useSelector(state => state?.viewListing?.maximumStay);
  const startDate = useSelector(state => selector(state, 'startDate'));
  const endDate = useSelector(state => selector(state, 'endDate'));
  const selectedGuests = useSelector(state => selector(state, 'personCapacity'));

  useEffect(() => {
    return () => {
      var root = document.getElementsByTagName('html')[0];
      root.classList.remove('scrollHidden');
    }
  })

  const renderGuests = (personCapacity) => {
    let rows = [];
    for (let i = 1; i <= personCapacity; i++) {
      rows.push(<option key={i} value={i}>{i} {i > 1 ? formatMessage(messages.guests) : formatMessage(messages.guest)}</option>);
    }
    return rows;
  }

  const renderFormControlSelect = ({ input, label, meta: { touched, error }, children, className }) => {
    return (
      <div>
        <FormControl componentClass="select" {...input} className={className} >
          {children}
        </FormControl>
        {touched && error && <span className={s.errorMessage}>{formatMessage(error)}</span>}
      </div>
    )
  }

  const renderWarningBlock = (content, error) => {
    let bgClass;
    if (error) {
      bgClass = s.alertBlockError;
    } else {
      bgClass = s.alertBlockSuccess;
    }
    return (
      isLoading ? <div className={cx(s.alertBlock, s.space4)}><Loader type={'text'} /></div> :
        <div className={cx(s.alertBlock, bgClass, s.space4)}>
          {content}
        </div>
    );
  }

  const onSubmit = async (values, dispatch) => {
    const isDateChosen = startDate != null && endDate != null;
    const disabled = !isDateChosen || !availability;
    if (!disabled) {
      dispatch(contactHostClose());
      const guests = !isNaN(parseInt(selectedGuests)) ? parseInt(selectedGuests) : 1;
      dispatch(directBookingRequest(id, guests, startDate, endDate, values.content));
    }
  }

  const { id, personCapacity, minNight, maxNight, maxDaysNotice, blockedDates, country, isMigrated } = props;
  const { profileId, picture, displayName, city } = props;
  const { handleSubmit, submitting } = props;

  let isDateChosen = startDate != null && endDate != null || false, disabled, loadingStatus;
  disabled = (!isDateChosen || !availability) ? true : false;
  loadingStatus = isLoading || false;

  return (
    <div className={s.root}>
      <Modal show={showContactHostModal} onHide={() => dispatch(contactHostClose())} animation={false} className={('contactHostModal')}>
        <Modal.Header closeButton>
          <Modal.Title className={s.title}><FormattedMessage {...messages.contactHost} /></Modal.Title>
        </Modal.Header>
        <Modal.Body bsClass={s.logInModalBody}>
          <div className={cx(s.modalGrid)}>
            <div className={cx(s.leftGrid)}>
              <div className={s.alignCenter}>
                <Avatar
                  source={picture}
                  height={108}
                  width={108}
                  title={displayName}
                  className={s.profileAvatar}
                  withLink
                  linkClassName={cx(s.mediaPhoto, s.mediaRound)}
                  profileId={profileId}
                  isFromMigration={isMigrated}
                />
                <h5 className={s.hostName}>{displayName}</h5>
              </div>
              <hr className={s.divider} />
              <div>
                <p className={s.heading}><FormattedMessage {...messages.contactHostinfo1} />:</p>
                <ul className={cx(s.listText)}>
                  <li>
                    <TickSvgIcon />
                    <span>
                      <FormattedMessage {...messages.contactHostinfo2} /> {displayName} <FormattedMessage {...messages.contactHostinfo3} />
                    </span>
                  </li>
                  <li>
                    <TickSvgIcon />
                    <span>
                      <FormattedMessage {...messages.contactHostinfo4} /> {city}<span className={'questionIconRTL'}>?</span>  <FormattedMessage {...messages.contactHostinfo5} />
                    </span>
                  </li>
                  <li>
                    <TickSvgIcon />
                    <span>
                      <FormattedMessage {...messages.contactHostinfo6} />!
                    </span>
                  </li>
                </ul>
              </div>
            </div>
            <div>
              <Loader
                show={loadingStatus}
                type={"page"}
              >
                <div>
                  {
                    !isDateChosen && renderWarningBlock(<FormattedMessage {...messages.hostErrorMessage1} />)
                  }
                  {
                    !maximumStay && !availability && isDateChosen && renderWarningBlock(<FormattedMessage {...messages.hostErrorMessage2} />, "error")
                  }
                  {
                    isDateChosen && maximumStay && renderWarningBlock(<div><FormattedMessage {...messages.maximumStay} /> {maxNight} <FormattedMessage {...messages.nights} /></div>, "error")
                  }
                  {
                    availability && isDateChosen && renderWarningBlock(<FormattedMessage {...messages.hostErrorMessage3} />)
                  }
                  <div>
                    <label className={s.labelText}><FormattedMessage {...messages.contactHostDate} /></label>
                    <div>
                      <Form onSubmit={handleSubmit(onSubmit)}>
                        <div className={cx('contactHost', s.space3)}>
                          <DateRange
                            listId={id}
                            minimumNights={minNight}
                            maximumNights={maxNight}
                            blockedDates={blockedDates}
                            formName={"ContactHostForm"}
                            maxDaysNotice={maxDaysNotice}
                            country={country}
                          />
                        </div>
                        <label className={s.labelText}><FormattedMessage {...messages.guests} /></label>
                        <Field name="personCapacity" component={renderFormControlSelect} className={cx(bt.commonControlSelect, 'commonControlSelectRtl')} >
                          <option value="">{formatMessage(messages.chooseGuests)}</option>
                          {renderGuests(personCapacity)}
                        </Field>
                        <div className={s.spaceTop3}>
                          <label className={s.labelText}><FormattedMessage {...messages.messages} /></label>
                          <Field
                            name="content"
                            component={renderFormControlTextArea}
                            className={s.textBox}
                            placeholder={formatMessage(messages.textBoxMessage)}
                          />
                        </div>
                        <Button className={cx(bt.btnPrimary, bt.btnLarge, bt.fullWidth, s.spaceTop4)} type="submit" disabled={submitting || disabled}>
                          <FormattedMessage {...messages.sendMessage} />
                        </Button>
                      </Form>
                    </div>
                  </div>
                </div>
              </Loader>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}


ContactHost.propTypes = {
  showContactHostModal: PropTypes.bool.isRequired,
  id: PropTypes.number.isRequired,
  userId: PropTypes.string.isRequired,
  city: PropTypes.string.isRequired,
  displayName: PropTypes.string.isRequired,
  picture: PropTypes.string,
  profileId: PropTypes.number.isRequired,
  personCapacity: PropTypes.number.isRequired,
  minNight: PropTypes.number,
  maxNight: PropTypes.number,
  maxDaysNotice: PropTypes.string,
  blockedDates: PropTypes.array,
  availability: PropTypes.bool,
  startDate: PropTypes.object,
  endDate: PropTypes.object,
  formatMessage: PropTypes.any,
  maximumStay: PropTypes.bool,
  country: PropTypes.string
};

ContactHost.defaultProps = {
  showContactHostModal: false,
  id: 0,
  userId: null,
  profileId: 0,
  city: null,
  displayName: null,
  picture: null,
  personCapacity: 0,
  minNight: 0,
  maxNight: 0,
  blockedDates: [],
  availability: false,
  startDate: null,
  endDate: null,
  maximumStay: false,
  country: null
};

export default reduxForm({
  form: "ContactHostForm",
  validate
})(withStyles(s, bt)(ContactHost));

